// Shared domain types. Field names follow the backend JSON returned to
// flutter_client (see its model classes) so responses map directly.

export type LatLon = {
  lat: number;
  lon: number;
};

export type RoutePoint = LatLon & {
  address: string;
};

export type RegionData = {
  countryCode: string;
  currencyCode: string;
  languageCode: string;
  cityId: string;
  districtId: string | null;
  h3Code?: string | null;
};

// Profile — subset of flutter_client ProfileModel (/client/profile).
export type Profile = {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  photo: string;
  rating: number;
};

// One tariff option from POST /route-and-rates -> data.rates[].
export type Rate = {
  rate: string; // carClassId (UUID)
  estimatedPrice: number;
  price: number;
  priceBeforeDiscount: number;
  // Server base price before any discount — used to reconstruct the struck-through
  // price when the selected rate is combined with services/priceRised. Mirrors
  // flutter_client RateModel.originalBasePrice.
  originalBasePrice: number;
  // Discounted base used as the starting point for price recalculation
  // (services/priceRised added on top). Mirrors RateModel.localPriceCalculate.
  localPriceCalculate: number;
  discount: number;
  compensation: number;
  compensationType: number;
  highDemandRatio?: number;
  vehicleType?: string; // display label derived from rate UUID (flutter_client VehicleType)
};

// Additional service (order preference) from GET /order/catalog/preferences.
// Mirrors flutter_client OrderPreferenceOption / backend OrderPreferenceModel.
export type OrderPreference = {
  id: string;
  key: string;
  name: string;
  price: number;
};

// User discount from POST {rpcBaseUrl}/get-discount-by-phone-number-and-origin-point.
// Mirrors flutter_client DiscountData. `discount`/`maxValues` are keyed by rate id;
// the rider app applies the first value as a single discount across all rates.
export type DiscountData = {
  discountType: number; // 0 = none, 1 = fixed amount, 2 = percent
  discount: Record<string, number>;
  maxValues: Record<string, number>;
  validUntil?: string | null;
};

// Route geometry + metrics from POST /route-and-rates -> data.route.
export type RouteInfo = {
  geometry: string; // encoded polyline
  distance: number; // meters
  duration: number; // seconds
};

export type RouteAndRates = {
  route: RouteInfo;
  rates: Rate[];
  addresses?: Array<{ full?: string; short?: string }>;
  discountType?: number;
  compensationType?: number;
};

// Address search suggestion from POST /places/autocomplete.
export type PlaceSuggestion = {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
  types: string[];
};

// Active/created order — subset of flutter_client CurrentOrderModel /
// OrderDetailsModel. The GET /order/rider/{orderId} response carries the full
// order object, so driver info (photo/name/car/plate/rating/phone) is populated
// by the same poll once a driver is assigned — no separate endpoint needed.
export type CurrentOrder = {
  id: string;
  statusId: number;
  priceEstimated: number;
  carClassId?: string;
  paymentTypeId?: number;
  driverId?: string | null;
  // Driver + car info (populated after assignment).
  driverName?: string | null;
  driverPhoto?: string | null;
  driverPhone?: string | null;
  driverRating?: number | null;
  carName?: string | null;
  carNumber?: string | null;
  carColorName?: string | null;
  carPhoto?: string | null;
};

// Driver offer (auction) from GET /order/rider/orders/{orderId}/offers.
// Mirrors flutter_client DriverOfferModel. The rider accepts one to assign
// the driver; rejecting/dismissed hides it and POSTs reject-offer.
export type Offer = {
  id: string;
  orderId: string;
  driverId: string;
  price: number;
  driverName?: string | null;
  driverPhoto?: string | null;
  driverRating?: number | null;
  carName?: string | null;
  carNumber?: string | null;
  carColorName?: string | null;
  carPhoto?: string | null;
  expiresAt?: string | null;
};

// Nearby free driver (ether) from GET /client/searchdriver/nearby-drivers.
// Mirrors flutter_client DriverLocationModel. Shown as car markers on the map
// while the user is building a route / searching.
export type NearbyDriver = {
  driverId: string;
  lat: number;
  lon: number;
  photo?: string | null;
  carPhoto?: string | null;
  color?: string | null;
};

// Payment types — flutter_client paymentTypeId mapping.
export const PaymentType = {
  Cash: 0,
  Card: 1,
  CashBonus: 2,
  GPay: 3,
  CardBonus: 4,
  ApplePay: 5,
} as const;
